import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtMoney, shortDate } from "../lib/format";
import { toast } from "sonner";
import {
  Upload,
  CheckCircle2,
  AlertCircle,
  MinusCircle,
  ArrowLeft,
  Sparkles,
} from "lucide-react";
import { useAccounts } from "../lib/accounts";

const CATEGORY_OPTIONS = [
  "Food", "Groceries", "Transport", "Shopping", "Entertainment",
  "Bills", "Subscriptions", "Gifts", "Income", "Other",
];

/** Renders a single row inside one of the three result buckets. */
function Section({ title, subtitle, count, tone, children, testid }) {
  const bg =
    tone === "danger"
      ? "#fdecc8"
      : tone === "warn"
        ? "#e5f0ff"
        : tone === "good"
          ? "var(--positive)"
          : "#fff";
  return (
    <div className="nb-card p-0 overflow-hidden" data-testid={testid}>
      <div className="p-3 border-b-2 border-[#1a1a1a]/10" style={{ background: bg }}>
        <div className="font-display font-black text-sm flex items-center gap-1.5">
          {title}
          <span className="ml-auto text-[11px] font-bold text-[#4a4a4a]">
            {count}
          </span>
        </div>
        <div className="text-[11px] font-medium text-[#4a4a4a] mt-0.5">
          {subtitle}
        </div>
      </div>
      {count === 0 ? (
        <div className="p-3 text-[11px] font-medium text-[#4a4a4a] italic">
          Nothing here — good.
        </div>
      ) : (
        <div className="divide-y-2 divide-[#1a1a1a]/10">{children}</div>
      )}
    </div>
  );
}

export default function CsvDiff() {
  const { selectedId, current } = useAccounts();
  const [csvText, setCsvText] = useState("");
  const [filename, setFilename] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addingIds, setAddingIds] = useState(new Set());
  const [fixingIds, setFixingIds] = useState(new Set());

  const onFile = async (f) => {
    if (!f) return;
    const text = await f.text();
    setCsvText(text);
    setFilename(f.name);
    setReport(null);
  };

  const runDiff = async () => {
    if (!csvText) {
      toast.error("Upload a CSV first");
      return;
    }
    if (!selectedId) {
      toast.error("Pick an account first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/reconcile/csv-diff", {
        csv_text: csvText,
        account_id: selectedId,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      setReport(data);
      const { missing_from_sb, amount_mismatches, extras_in_sb, exact_matches } = data.summary;
      toast.success(
        `${exact_matches} ✓ · ${missing_from_sb} missing · ${amount_mismatches} amount off · ${extras_in_sb} extra`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diff failed");
    } finally {
      setBusy(false);
    }
  };

  const addMissing = async (row, category) => {
    if (!selectedId) return;
    const key = `add-${row.row_index}`;
    setAddingIds((s) => new Set(s).add(key));
    try {
      await api.post("/transactions", {
        account_id: selectedId,
        date: row.date,
        payee: row.description || "Bank import",
        amount: Number(row.amount),
        type: row.type,
        category: category || "Other",
        memo: "Added via CSV diff",
        cleared: true,
      });
      // Remove from missing list locally
      setReport((r) => {
        if (!r) return r;
        const missing = r.missing_from_sb.filter((x) => x.row_index !== row.row_index);
        return {
          ...r,
          missing_from_sb: missing,
          summary: { ...r.summary, missing_from_sb: missing.length },
        };
      });
      toast.success("Added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setAddingIds((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
    }
  };

  const fixAmount = async (mm) => {
    const key = `fix-${mm.sb.id}`;
    setFixingIds((s) => new Set(s).add(key));
    try {
      await api.put(`/transactions/${mm.sb.id}`, {
        amount: Number(mm.bank.amount),
      });
      setReport((r) => {
        if (!r) return r;
        const remaining = r.amount_mismatches.filter((x) => x.sb.id !== mm.sb.id);
        return {
          ...r,
          amount_mismatches: remaining,
          summary: { ...r.summary, amount_mismatches: remaining.length },
        };
      });
      toast.success("Amount updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fix failed");
    } finally {
      setFixingIds((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
    }
  };

  const totalMissing = useMemo(() => {
    if (!report) return 0;
    return report.missing_from_sb.reduce((a, r) => {
      return a + (r.type === "deposit" ? Number(r.amount) : -Number(r.amount));
    }, 0);
  }, [report]);

  const [undoing, setUndoing] = useState(false);
  const undoRecent = async () => {
    if (!window.confirm("Delete every transaction added via CSV Diff in the last 7 days?")) {
      return;
    }
    setUndoing(true);
    try {
      const { data } = await api.post("/reconcile/csv-diff/undo-recent");
      toast.success(
        data.deleted === 0
          ? "Nothing to undo — no recent CSV Diff additions found."
          : `Deleted ${data.deleted} row${data.deleted === 1 ? "" : "s"} · net reversed ${data.net_reversed >= 0 ? "+" : "-"}$${Math.abs(data.net_reversed).toFixed(2)}`
      );
      setReport(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="csv-diff-page">
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/reconcile"
            className="inline-flex items-center gap-1 text-[11px] font-bold text-[#4a4a4a] hover:text-[#1a1a1a]"
            data-testid="back-to-reconcile"
          >
            <ArrowLeft size={12} strokeWidth={3} /> Back to Reconcile
          </Link>
          <h1 className="font-display text-3xl font-black">Compare bank CSV</h1>
        </div>
      </div>

      <div
        className="nb-card p-3 flex items-start gap-2 text-[12px] font-medium"
        style={{ background: "#e5f0ff" }}
      >
        <Sparkles size={14} strokeWidth={3} className="mt-0.5" />
        <div className="flex-1">
          Upload your bank CSV for a statement cycle. SyncBook will show you exactly which rows are
          missing, which have wrong amounts, and which are in SyncBook but not on the bank —
          then let you fix each in one click. Working on{" "}
          <b>{current?.name || "your account"}</b>.
        </div>
      </div>

      {/* Rescue: undo any additions made in the last 24h */}
      <button
        type="button"
        data-testid="undo-recent-diff-btn"
        onClick={undoRecent}
        disabled={undoing}
        className="nb-btn nb-btn-secondary w-full !py-2 text-sm"
      >
        <MinusCircle size={14} strokeWidth={3} />
        {undoing ? "Undoing…" : "Undo recent CSV Diff additions (last 7 days)"}
      </button>

      {/* Uploader + date range */}
      <div className="nb-card-lg p-4 space-y-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
            Bank CSV file
          </label>
          <label
            className="mt-1 border-2 border-dashed border-[#1a1a1a] rounded-xl p-4 flex items-center gap-2 cursor-pointer hover:bg-[#f4f0eb] transition"
            data-testid="csv-file-drop"
          >
            <Upload size={16} strokeWidth={3} />
            <div className="flex-1 text-sm font-medium">
              {filename || "Choose a CSV file…"}
            </div>
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="csv-file-input"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
              Statement start (optional)
            </label>
            <input
              data-testid="diff-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="nb-input !text-sm !py-2"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
              Statement end (optional)
            </label>
            <input
              data-testid="diff-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="nb-input !text-sm !py-2"
            />
          </div>
        </div>

        <button
          type="button"
          onClick={runDiff}
          disabled={busy || !csvText}
          data-testid="run-diff-btn"
          className="nb-btn nb-btn-dark w-full !py-3"
        >
          {busy ? "Comparing…" : "Compare CSV to SyncBook"}
        </button>
      </div>

      {/* Report */}
      {report && (
        <div className="space-y-3" data-testid="diff-report">
          {/* Summary tiles */}
          <div className="grid grid-cols-4 gap-2 text-center">
            {[
              { label: "Matches", value: report.summary.exact_matches, testid: "sum-matches" },
              { label: "Missing", value: report.summary.missing_from_sb, testid: "sum-missing" },
              { label: "Amount off", value: report.summary.amount_mismatches, testid: "sum-mismatch" },
              { label: "Extra in SB", value: report.summary.extras_in_sb, testid: "sum-extras" },
            ].map((tile) => (
              <div
                key={tile.label}
                data-testid={tile.testid}
                className="p-2 border-2 border-[#1a1a1a] rounded-xl bg-white"
              >
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
                  {tile.label}
                </div>
                <div className="font-display font-black text-xl">{tile.value}</div>
              </div>
            ))}
          </div>

          {/* Missing from SB — biggest lever */}
          <Section
            testid="section-missing"
            tone="danger"
            title="🔴 On bank statement, NOT in SyncBook"
            subtitle={`Add these to close a gap of ${fmtMoney(Math.abs(totalMissing))} net. One-tap add per row.`}
            count={report.missing_from_sb.length}
          >
            {report.missing_from_sb.map((row) => {
              const key = `add-${row.row_index}`;
              const isAdding = addingIds.has(key);
              return (
                <div
                  key={row.row_index}
                  data-testid={`missing-row-${row.row_index}`}
                  className="p-3 flex items-center gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm truncate">
                      {row.description || "(no description)"}
                    </div>
                    <div className="text-[11px] font-medium text-[#4a4a4a]">
                      {shortDate(row.date)} · {row.type}
                    </div>
                  </div>
                  <div
                    className={`font-display font-black text-lg ${
                      row.type === "deposit" ? "balance-pos" : ""
                    }`}
                  >
                    {row.type === "deposit" ? "+" : "-"}
                    {fmtMoney(row.amount)}
                  </div>
                  <button
                    type="button"
                    data-testid={`add-missing-${row.row_index}`}
                    disabled={isAdding}
                    onClick={() => addMissing(row, row.type === "deposit" ? "Income" : "Other")}
                    className="nb-btn nb-btn-secondary !py-1.5 !px-3 !text-xs"
                  >
                    {isAdding ? "…" : "+ Add"}
                  </button>
                </div>
              );
            })}
          </Section>

          {/* Amount mismatches — high-value fixes */}
          <Section
            testid="section-mismatch"
            tone="warn"
            title="🟡 Amount doesn't match"
            subtitle="Bank & SyncBook likely refer to the same row but the amount differs. One tap to sync SyncBook to bank."
            count={report.amount_mismatches.length}
          >
            {report.amount_mismatches.map((mm) => {
              const key = `fix-${mm.sb.id}`;
              const isFixing = fixingIds.has(key);
              return (
                <div
                  key={mm.sb.id}
                  data-testid={`mismatch-row-${mm.sb.id}`}
                  className="p-3 space-y-1"
                >
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-sm truncate">
                        {mm.sb.payee || mm.bank.description || "(no payee)"}
                      </div>
                      <div className="text-[11px] font-medium text-[#4a4a4a]">
                        SB {shortDate(mm.sb.date)} · Bank {shortDate(mm.bank.date)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-bold uppercase text-[#4a4a4a]">SB</div>
                      <div className="font-display font-black text-sm">
                        {fmtMoney(mm.sb.amount)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-bold uppercase text-[#4a4a4a]">Bank</div>
                      <div className="font-display font-black text-sm balance-pos">
                        {fmtMoney(mm.bank.amount)}
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid={`fix-mismatch-${mm.sb.id}`}
                      disabled={isFixing}
                      onClick={() => fixAmount(mm)}
                      className="nb-btn nb-btn-dark !py-1.5 !px-3 !text-xs"
                    >
                      {isFixing ? "…" : "Sync"}
                    </button>
                  </div>
                  <div className="text-[11px] font-medium text-[#4a4a4a]">
                    Δ {mm.delta >= 0 ? "+" : "-"}
                    {fmtMoney(Math.abs(mm.delta))}
                  </div>
                </div>
              );
            })}
          </Section>

          {/* Extras — in SB but not on bank */}
          <Section
            testid="section-extras"
            tone="warn"
            title="🟠 In SyncBook, NOT on bank"
            subtitle="These cleared txns didn't appear on this CSV. Could be from a different cycle, an intra-day duplicate, or a mistakenly cleared pending item."
            count={report.extras_in_sb.length}
          >
            {report.extras_in_sb.map((t) => (
              <Link
                key={t.id}
                to={`/txn/${t.id}`}
                data-testid={`extra-row-${t.id}`}
                className="p-3 flex items-center gap-3 hover:bg-white/50"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm truncate">{t.payee}</div>
                  <div className="text-[11px] font-medium text-[#4a4a4a]">
                    {shortDate(t.date)} · {t.category}
                  </div>
                </div>
                <div
                  className={`font-display font-black text-sm ${
                    t.type === "deposit" ? "balance-pos" : ""
                  }`}
                >
                  {t.type === "deposit" ? "+" : "-"}
                  {fmtMoney(t.amount)}
                </div>
              </Link>
            ))}
          </Section>

          {/* Matches summary — collapsed reassurance */}
          <div
            className="nb-card p-3 flex items-center gap-2"
            style={{ background: "var(--positive)" }}
          >
            <CheckCircle2 size={14} strokeWidth={3} />
            <div className="text-[12px] font-medium">
              <b>{report.summary.exact_matches}</b> row{report.summary.exact_matches === 1 ? "" : "s"}{" "}
              matched exactly.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
